// modernSocialDockResize — Modern 中右ドックの メンバー⇄チャット間 drag-resize
// splitter の純粋関数群.
//
// ModernSocialDock.tsx は pointer events / DOM / setPointerCapture を扱うので
// vitest (node env, no DOM) では直接 test できない. ここで「pointer 座標 →
// member 欄高さ変換」と「チャットが潰れないよう動的上限を掛けた clamp」を
// 純粋関数に切り出して単体テストに乗せる (previewOverlay.ts / colorWheel.ts 流儀).
//
// 永続化は infrastructure/paneSizePreference.ts の Modern member-height key に
// 委譲する (clamp の固定上限 480 / 下限 120 はそちら). 本モジュールは「チャット
// 入力欄が常時可視」を担保するための **動的上限** (= ドック全高に対して chat に
// 最低高さを残す) を足す役割.

import {
  clampModernMemberHeightPx,
  MODERN_MEMBER_HEIGHT_PX_MIN,
} from '../../../infrastructure/paneSizePreference.js';

/**
 * チャット欄に残す最低高さ (px). この分はメンバー欄に渡さず、入力欄 + ログ数行が
 * 常時可視であることを保証する. メンバー欄の動的上限 = ドック内 member+chat の
 * 利用可能高さ - この値.
 */
export const CHAT_MIN_HEIGHT_PX = 140;

/**
 * メンバー欄高さを `[下限, min(固定上限, 動的上限)]` に閉じ込める.
 *
 * - 固定上限 / 下限 / NaN フォールバックは `clampModernMemberHeightPx` に委譲.
 * - `availableHeightPx` (= member + splitter + chat に使える縦寸) が分かるときは
 *   `availableHeightPx - CHAT_MIN_HEIGHT_PX` を動的上限として更に切り詰める
 *   (= チャットが潰れない保証). 取得できない (= 0 以下 / 非有限) ときは動的上限を
 *   掛けず固定 clamp のみ.
 *
 * 下限割れを避けるため、動的上限が下限を下回る狭小ドックでは下限を優先する.
 */
export const clampModernMemberHeightForDock = (
  desiredPx: number,
  availableHeightPx: number,
): number => {
  const base = clampModernMemberHeightPx(desiredPx);
  if (!Number.isFinite(availableHeightPx) || availableHeightPx <= 0) {
    return base;
  }
  const dynamicMax = availableHeightPx - CHAT_MIN_HEIGHT_PX;
  // 動的上限が下限未満 (= 極端に低いドック) では下限を維持しチャット側に任せる.
  if (dynamicMax <= MODERN_MEMBER_HEIGHT_PX_MIN) {
    return MODERN_MEMBER_HEIGHT_PX_MIN;
  }
  return Math.min(base, dynamicMax);
};

/**
 * splitter drag の pointer Y から新しいメンバー欄高さを算出.
 *
 * メンバー欄は splitter の **上** に在るので、`高さ = pointerY - メンバー欄上端Y`.
 * 下にドラッグ = メンバー拡大 / チャット縮小、上 = 逆 (= 直感どおり).
 * 動的上限つき clamp を通して返す.
 */
export const memberHeightFromPointer = (
  pointerClientY: number,
  memberTopY: number,
  availableHeightPx: number,
): number => clampModernMemberHeightForDock(pointerClientY - memberTopY, availableHeightPx);
